import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { query, type IndicadorsDto } from './activacions';
import type { EstatMonitoratge, RegistreAuditoriaDto } from './administracio';
import { apiDescarrega, apiRequest, apiRequestAmbEtag } from './client';
import type { Pagina } from './types';

export type TipusInforme = 'DETALL' | 'GLOBAL' | 'EQUIP' | 'AUDITORIA';
export type TipusProgramat = 'GLOBAL' | 'COMPARATIVA' | 'ACTIVACIONS' | 'RECURRENTS' | 'INDICADORS';
export type FormatInforme = 'PDF' | 'CSV' | 'XLSX';
export type SeccioInforme = 'DESTINATARIS' | 'CANALS' | 'TEMPS' | 'MISSATGES' | 'ACCIONS';
export type Periodicitat = 'SETMANAL' | 'MENSUAL' | 'TRIMESTRAL';

export const SECCIONS: SeccioInforme[] = [
  'DESTINATARIS',
  'CANALS',
  'TEMPS',
  'MISSATGES',
  'ACCIONS',
];
export const TIPUS_INFORME: TipusInforme[] = ['DETALL', 'GLOBAL', 'EQUIP', 'AUDITORIA'];
export const PERIODICITATS: Periodicitat[] = ['SETMANAL', 'MENSUAL', 'TRIMESTRAL'];

export const TIPUS_PROGRAMAT: Record<TipusInforme, TipusProgramat | null> = {
  DETALL: 'ACTIVACIONS',
  GLOBAL: 'GLOBAL',
  EQUIP: 'COMPARATIVA',
  AUDITORIA: null,
};

export const CRON: Record<Periodicitat, string> = {
  SETMANAL: '0 7 * * 1',
  MENSUAL: '0 7 1 * *',
  TRIMESTRAL: '0 7 1 1,4,7,10 *',
};

export function periodicitatDeCron(cron: string): Periodicitat | null {
  const p = (Object.keys(CRON) as Periodicitat[]).find((k) => CRON[k] === cron.trim());
  return p ?? null;
}

export interface FiltresIndicadors {
  desde?: string;
  fins?: string;
  departamentId?: number | '';
  areaId?: number | '';
  serveiId?: number | '';
  tipus?: string;
}

export interface InformeProgramatDto {
  id?: number | null;
  nom: string;
  tipus: TipusProgramat;
  filtres: Record<string, string>;
  seccions: string[];
  format: FormatInforme;
  cron: string;
  destinataris: string[];
  propietariId?: number | null;
  actiu: boolean;
  ultimaExecucio?: string | null;
  createdAt?: string | null;
  version?: number;
}

export interface EventTracabilitat {
  at: string;
  tipus: string;
  detall?: string | null;
  usuariId?: number | null;
  usuari?: string | null;
  destinatariId?: number | null;
  canal?: string | null;
}

export interface TempsTracabilitat {
  indicador: string;
  compromesSeg?: number | null;
  realSeg?: number | null;
  complert?: boolean | null;
}

export interface TracabilitatDto {
  activacioId: number;
  codi: string;
  cronologia: EventTracabilitat[];
  temps: TempsTracabilitat[];
}

export interface FiltresAuditoria {
  desde?: string;
  fins?: string;
  usuariId?: number | '';
  accio?: string;
  entitat?: string;
  entitatId?: string;
  resultat?: string;
  page?: number;
  size?: number;
}

export function useIndicadorsFiltrats(filtres: FiltresIndicadors, enabled = true) {
  return useQuery({
    queryKey: ['indicadors', 'filtrats', filtres],
    queryFn: () => apiRequest<IndicadorsDto>(`/informes/indicadors${query(filtres)}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useIndicadorsArees(areaIds: number[], desde: string, fins: string) {
  return useQueries({
    queries: areaIds.map((areaId) => ({
      queryKey: ['indicadors', 'filtrats', { desde, fins, areaId }],
      queryFn: () =>
        apiRequest<IndicadorsDto>(`/informes/indicadors${query({ desde, fins, areaId })}`),
    })),
  });
}

const CLAU_PROGRAMATS = ['informes', 'programats'] as const;

export function useInformesProgramats() {
  return useQuery({
    queryKey: CLAU_PROGRAMATS,
    queryFn: () => apiRequest<InformeProgramatDto[]>('/informes/programats'),
  });
}

/**
 * Desa un informe programat. En modificar, `etag` ha de ser el del GET que va carregar el
 * formulari (useAmbEtag): si algú altre l'ha canviat mentrestant el servidor respon 412.
 */
export function useDesaInformeProgramat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      informe,
      etag,
    }: {
      informe: InformeProgramatDto;
      etag?: string | null;
    }) => {
      if (informe.id == null) {
        return apiRequest<InformeProgramatDto>('/informes/programats', {
          method: 'POST',
          json: informe,
        });
      }
      return apiRequest<InformeProgramatDto>(`/informes/programats/${informe.id}`, {
        method: 'PUT',
        json: informe,
        headers: etag ? { 'If-Match': etag } : undefined,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAU_PROGRAMATS }),
  });
}

/**
 * Activa o desactiva un informe programat des de la llista. Es llegeix la versió vigent del
 * servidor i només se'n canvia `actiu`, enviant l'ETag d'aquesta mateixa lectura: no es
 * reescriuen camps amb dades antigues de la llista i un canvi concurrent entre la lectura i
 * l'escriptura acaba en 412.
 */
export function useCommutaInformeProgramat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, actiu }: { id: number; actiu: boolean }) => {
      const ruta = `/informes/programats/${id}`;
      const { data, etag } = await apiRequestAmbEtag<InformeProgramatDto>(ruta);
      return apiRequest<InformeProgramatDto>(ruta, {
        method: 'PUT',
        json: { ...data, actiu },
        headers: etag ? { 'If-Match': etag } : undefined,
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CLAU_PROGRAMATS }),
  });
}

export function useEliminaInformeProgramat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<void>(`/informes/programats/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAU_PROGRAMATS }),
  });
}

export function useAuditoria(filtres: FiltresAuditoria) {
  return useQuery({
    queryKey: ['auditoria', filtres],
    queryFn: () => apiRequest<Pagina<RegistreAuditoriaDto>>(`/auditoria${query(filtres)}`),
    placeholderData: keepPreviousData,
  });
}

export function descarregaAuditoria(filtres: FiltresAuditoria) {
  return apiDescarrega(
    `/auditoria${query({ ...filtres, page: undefined, size: undefined, format: 'csv' })}`,
    'auditoria.csv',
  );
}

/**
 * Monitoratge d'enviaments de la finestra mòbil `finestraMs` (24 h per defecte). L'inici de la
 * finestra es calcula a cada petició, de manera que cada refresc automàtic consulta les darreres
 * 24 h reals i no les del moment en què es va obrir la pantalla.
 */
export function useMonitoratgeEnviaments(
  filtres: { canal?: string; estat?: string },
  finestraMs = 86_400_000,
) {
  return useQuery({
    queryKey: ['monitoratge', 'enviaments', filtres, finestraMs],
    queryFn: () =>
      apiRequest<EstatMonitoratge>(
        `/monitoratge/enviaments${query({
          ...filtres,
          desde: new Date(Date.now() - finestraMs).toISOString(),
        })}`,
      ),
    refetchInterval: 30_000,
  });
}

export function useTracabilitat(id: number) {
  return useQuery({
    queryKey: ['activacio', id, 'tracabilitat'],
    queryFn: () => apiRequest<TracabilitatDto>(`/activacions/${id}/tracabilitat`),
    enabled: Number.isFinite(id),
  });
}

export function descarregaInformeActivacio(
  id: number,
  codi: string,
  format: FormatInforme,
  idioma: string,
) {
  const f = format.toLowerCase();
  return apiDescarrega(`/activacions/${id}/informe${query({ format: f, idioma })}`, `${codi}.${f}`);
}

export function iniciPeriode(periode: 'SETMANA' | 'MES' | 'TRIMESTRE', ara = new Date()): Date {
  const dies = periode === 'SETMANA' ? 7 : periode === 'MES' ? 30 : 90;
  return new Date(ara.getTime() - dies * 86_400_000);
}
